// src/api/axiosInstance.ts
import axios, { AxiosResponse, AxiosRequestConfig } from "axios";
import { snackbarStore } from "src/store/snackbarStore";
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from "src/utils/tokenCookie";

export const BASE_URL = process.env.REACT_APP_OMS_SERVER_API;
// process.env.OMS_SERVER_API ||
// process.env.REACT_APP_OMS_SERVER_API ||
// process.env.API_BASE_URL ||
// process.env.REACT_APP_API_BASE_URL ||
// process.env.REACT_APP_SERVER_URL ||
// "http://localhost:8080";

export const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  // withCredentials: true, //쿠키 전송 필요x
});

type RetryConfig = AxiosRequestConfig & { _retry?: boolean };

// =======================
// 요청 인터셉터
// =======================
axiosInstance.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set("Authorization-OMS", `Bearer ${token}`);
  }

  // 디버깅용 로그
  console.log("Request headers:", config.headers);
  console.log("Request data type:", config.data?.constructor?.name);

  return config;
});

// =======================
// refresh 동시성 제어
// =======================
let isRefreshing = false;
let waiters: Array<(token: string) => void> = [];

const waitForRefresh = () =>
  new Promise<string>((resolve) => {
    waiters.push(resolve);
  });

const notifyWaiters = (token: string) => {
  waiters.forEach((r) => r(token));
  waiters = [];
};

async function refreshAccessToken(): Promise<string> {
  const rt = getRefreshToken();
  if (!rt) throw new Error("NO_REFRESH_TOKEN");

  const res = await axios.post(
    `${BASE_URL}/v1/admin/auth/refresh`,
    { refreshToken: rt },
    {
      headers: { "Content-Type": "application/json" },
      // withCredentials: true,
    }
  );

  const { accessToken, refreshToken } = res.data?.data || {};
  if (!accessToken) throw new Error("INVALID_REFRESH_RESPONSE");

  setTokens(accessToken, refreshToken);
  return accessToken;
}

// =======================
// 응답 인터셉터
// =======================
axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => res,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error.config as RetryConfig;

    const reqUrl = String(originalRequest?.url ?? "");
    const isBrowser = typeof window !== "undefined";
    const pathname = isBrowser ? window.location.pathname : "";

    const isRefreshApi = reqUrl.includes("/auth/refresh");
    const isLoginApi = reqUrl.includes("/auth/google/token");
    const isLoginPage = pathname.startsWith("/login");

    // =======================
    // ❌ refresh API에서 401 → 즉시 로그아웃
    // =======================
    if (status === 401 && isRefreshApi) {
      clearTokens();
      if (isBrowser) window.location.replace("/login");
      return Promise.reject(error);
    }

    // =======================
    // ❌ 로그인/콜백 중 401 → 리다이렉트 금지
    // =======================
    if (status === 401 && (isLoginApi || isLoginPage)) {
      return Promise.reject(error);
    }

    // =======================
    // ✅ 일반 API 401 → refresh 시도
    // =======================
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        if (isRefreshing) {
          const newToken = await waitForRefresh();
          originalRequest.headers = originalRequest.headers ?? {};
          (originalRequest.headers as any)["Authorization-OMS"] = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        }

        isRefreshing = true;
        const newToken = await refreshAccessToken();
        notifyWaiters(newToken);

        originalRequest.headers = originalRequest.headers ?? {};
        (originalRequest.headers as any)["Authorization-OMS"] = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (e) {
        clearTokens();
        if (isBrowser) window.location.replace("/login");
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }

    // =======================
    // 공통 에러 스낵바
    // =======================
    snackbarStore.getState().addSnackbar({
      message: error?.response?.data?.message || "요청 중 오류가 발생했습니다.",
      severity: "error",
    });

    return Promise.reject(error);
  }
);
